import { AfterViewInit, Component, Inject, ViewChild } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { ApiViewerService } from "../../viewer-components/services/internal-services/api-viewer.service";
import { MatSelect } from "@angular/material/select";
import { Viewerlayer } from "../../viewer-components/model/viewerlayer";
import { GeoService } from "../../viewer-components/services/internal-services/geo.service";
import { ViewerConstants } from "../../shared/model/viewer-constants";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../shared/model/google-analytics-constants";
import { Subscription } from "rxjs";
import { ReportService } from "../../viewer-components/services/internal-services/report.service";
import { ICollection } from "../../viewer-components/interfaces/i-collection";
import { GoogleAnalyticsService } from "../../shared/services/google-analytics.service";
import { MapService } from "../../viewer-components/services/internal-services/map.service";

@Component({
  selector: "lib-damage-assessment-dialog",
  templateUrl: "./damage-assessment-dialog.component.html",
  styleUrls: ["./damage-assessment-dialog.component.scss"],
  providers: [MatDialogConfig, ApiViewerService, MapService],
  standalone: false,
})
export class DamageAssessmentDialogComponent implements AfterViewInit {
  aoiSelected;

  damageDialog;

  formSubmitted = false;

  availableAOIs = [];

  loading = true;

  progress = 0;

  viewerService;

  mapService;

  googleAnalyticsService: GoogleAnalyticsService;

  reportService: ReportService;

  subscriptions: Subscription[] = [];

  allFeatures;

  @ViewChild("eventSelector") eventSelector: MatSelect;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private matDialog: MatDialog,
  ) {}

  ngAfterViewInit(): void {
    this.damageDialog = this.matDialog.getDialogById("damage-assessment-dialog");
    this.viewerService = this.data.viewerService;
    this.googleAnalyticsService = this.data.googleAnalyticsService;
    this.mapService = this.data.mapService;
    this.reportService = this.data.reportService;
    this.retrieveDamageAssessment(this.data.layer);
  }

  submit(): void {
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MY_DATA_REVIEW_PIF_X_DAMAGE_ASSESSMENT,
      GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
      true,
    );
    this.formSubmitted = true;
  }

  closeDialog() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.damageDialog.close();
  }

  cancel() {
    this.formSubmitted = false;
  }

  getSummaryTitle() {
    return this.formSubmitted ? "Ready to start processing!" : "Retrieve damage assessment";
  }

  getSummaryDescription() {
    return this.formSubmitted
      ? "Are you sure you want to move forward with this assessment?"
      : "Retrieve damage assessment information for properties in affected areas on this list. A file with the appended information will automatically download for you to share with your organization.";
  }

  getDamageAssessment(aoiSelected: any) {
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MY_DATA_START_PROCESSING_PIF_X_DAMAGE_ASSESSMENT,
      GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
      true,
    );
    this.reportService.getDamageAssessment(aoiSelected);
    this.damageDialog.close();
  }

  retrieveDamageAssessment(layer: Viewerlayer) {
    this.allFeatures = layer.source.features.features;
    const bboxPolygon = GeoService.bbox(layer.source.features, true);
    const collections = this.mapService.findCollectionsIntersects(bboxPolygon, [
      ViewerConstants.LAYER_NAMES.GRAYSKY_G,
      ViewerConstants.LAYER_NAMES.GRAYSKY,
    ]) as ICollection[];
    // const availableCollections = [];
    for (const collection of collections) {
      const name = collection.properties["graysky-event"];
      const prettyName = collection.properties["graysky-event-pretty-name"];
      const year = this.getYearFromStringDate(collection.properties["first-capture-date"]);
      let found = this.availableAOIs.find((c) => c.prettyName === prettyName);
      if (year >= ViewerConstants.DAMAGE_ASSESSMENT_MIN_YEAR) {
        if (!found) {
          found = {
            name,
            parentName: name,
            prettyName: prettyName,
            date: collection.properties["first-capture-date"],
            features: [],
            geometries: [collection.geometry],
          };
          this.availableAOIs.push(found);
        } else {
          if (!found.geometries.includes(collection.geometry)) {
            found.geometries.push(collection.geometry);
          }
        }
      }
    }

    this.loading = false;
  }

  getYearFromStringDate(dateStr: string) {
    const date = new Date(dateStr);
    return date.getFullYear();
  }

  calculateFeatures(aoi) {
    this.loading = true;
    if (aoi.features.length === 0) {
      this.allFeatures.forEach((f) => {
        if (!aoi.features.includes(f)) {
          const intersects = aoi.geometries.some((g) => GeoService.polygonsIntersection(g, f.geometry));
          if (intersects) {
            const point = GeoService.pointToGeoJSONWGS84(f.geometry.coordinates);
            f.lon = point.geometry.coordinates[0];
            f.lat = point.geometry.coordinates[1];
            f.wkt = GeoService.pointToWkt(point.geometry.coordinates);
            aoi.features.push(f);
          }
        }
      });
    }

    this.loading = false;
  }
}
